import { client } from '@api/rest/client';
import {
  LLMUsageCheckResultStatus,
  type TCheckResult,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import {
  CONVERSATIONS_DEFAULT_STATE,
  ConversationsViewType,
  type TConversationsResponse,
  type TDataSourceGenerationResponse,
  type TLLMUsagesHistoryResponse,
  type TTranslateConversationResponse,
  type TWeeklyOverviewResponse,
  type TWeeklyStatisticsResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { TReportIssueParams } from '@conversed/rest/src/contracts/llm-usages/report-issue';
import { SortOption } from '@conversed/rest/src/contracts/llm-usages/sort-option.enum';
import { getFileName } from '@conversed/shared/src/helpers/utils';
import { getLast7DaysDateRange } from '@helpers/dateHelpers';
import { useFileCreation } from '@hooks/useFileCreation';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toastErrorTranslated } from '../../helpers/toast';
import { useFileDownload } from '../useFileDownload';

export const useLLMUsagesActions = () => {
  const dispatch = useDispatch();
  const { downloadXLS } = useFileDownload();
  const { createXLSXFile } = useFileCreation();
  const MAX_CELL_LENGTH = 32767;
  const REPLACEMENT_TEXT_FOR_CELL_MAXIMUM_LENGTH =
    '[CONTENT REMOVED: EXCEEDS CELL MAXIMUM LENGTH]';

  const checkAndReplaceLongField = useCallback((field: string): string => {
    return field.length > MAX_CELL_LENGTH
      ? REPLACEMENT_TEXT_FOR_CELL_MAXIMUM_LENGTH
      : field;
  }, []);

  const getLLMUsages = useCallback(
    async (
      startDate: string,
      endDate: string,
      configId: string,
      page?: number,
      perPage?: number,
    ): Promise<TLLMUsagesHistoryResponse> => {
      try {
        const queryParams = {
          configId,
          startDate,
          endDate,
        };

        if (page && perPage) {
          Object.assign(queryParams, {
            page: page.toString(),
            perPage: perPage.toString(),
          });
        }

        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.llmUsages.getLLMUsagesHistory({
          query: queryParams,
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return {
          histories: [],
          startDate: '',
          endDate: '',
          companyName: '',
          totalRecords: 0,
        };
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const downloadLLMUsagesHistory = useCallback(
    async (configId: string) => {
      try {
        // Get the start and end dates for the last 7 days
        const { startDate: last7DaysStartDate, endDate: last7DaysEndDate } =
          getLast7DaysDateRange();

        const { histories, startDate, endDate, companyName } =
          await getLLMUsages(last7DaysStartDate, last7DaysEndDate, configId);

        const worksheetData = histories?.map((item) => ({
          _Id: item._id,
          UserId: item.userId,
          CreatedAt: item.createdAt,
          Question: checkAndReplaceLongField(item.question),
          Answer: checkAndReplaceLongField(item.answer),
          URL: item.url,
          NumberOfButtonClick: item.url ? item?.numberOfButtonClick || 0 : null,
          Categories: item.categories,
          Keywords: item.keywords?.length ? item.keywords.join(', ') : '',
          ArticleIds: item.articleIds,
          ConfigId: item.configId || '',
        }));

        const file = createXLSXFile(worksheetData, 'Conversation History');
        const fileName = `${getFileName('qas', startDate, endDate, companyName)}.xlsx`;
        downloadXLS({ data: file, fileName: fileName });
      } catch (error) {
        console.error('Error downloading conversation history', error);
        toastErrorTranslated('error.codes.SERVER_ERROR');
      }
    },
    [downloadXLS, createXLSXFile, checkAndReplaceLongField, getLLMUsages],
  );

  const getConversationsByWeeks = useCallback(
    async (
      weeks: number[],
      configId: string,
      year: number,
      llmBlockId: string | undefined,
      onlyUnverifiedAnswers: boolean,
      sortOption: SortOption = SortOption.Newest,
      includeNotEvaluable = false,
      viewType: ConversationsViewType = ConversationsViewType.SINGLE,
      page?: number,
      perPage?: number,
      filterKeywords?: string[],
      hotTopicId?: string,
    ): Promise<TConversationsResponse> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const queryParams = {
          weeks: weeks.join(','),
          configId,
          llmBlockId,
          onlyUnverifiedAnswers: onlyUnverifiedAnswers.toString(),
          year: year.toString(),
          sortOption,
          includeNotEvaluable: includeNotEvaluable.toString(),
          viewType,
          // Only include keywords param when there is an active filter
          ...(filterKeywords && filterKeywords.length > 0
            ? { keywords: filterKeywords.join(',') }
            : {}),
          // Only include hotTopicId when a specific topic is selected
          ...(hotTopicId && hotTopicId !== 'all' ? { hotTopicId } : {}),
        };

        if (page && perPage) {
          Object.assign(queryParams, {
            page: page.toString(),
            perPage: perPage.toString(),
          });
        }

        const { data } = await client.llmUsages.getConversationsByWeeks({
          query: queryParams,
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return CONVERSATIONS_DEFAULT_STATE;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getConversationsByIds = useCallback(
    async (ids: string[]): Promise<TConversationsResponse> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.llmUsages.getConversationsByIds({
          body: {
            ids,
          },
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return CONVERSATIONS_DEFAULT_STATE;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getDataForDataSourceGeneration = useCallback(
    async (
      weeks: number[],
      configId: string,
      llmBlockId: string,
      year: number,
      hotTopicId?: string,
    ): Promise<TDataSourceGenerationResponse> => {
      try {
        const { data } = await client.llmUsages.getDataForDataSourceGeneration({
          query: {
            weeks: weeks.join(','),
            configId,
            llmBlockId,
            year: year.toString(),
            // Only include hotTopicId when a specific topic is selected
            ...(hotTopicId && hotTopicId !== 'all' ? { hotTopicId } : {}),
          },
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return {
          factCheckedData: [],
          duplicateQuestions: [],
          hyperlinksCorrectedCount: 0,
        };
      }
    },
    [],
  );

  const fetchLLMConversations = useCallback(
    async (
      startDate: string,
      endDate: string,
      configId: string,
      page: number,
      perPage: number,
    ) => {
      const data = await getLLMUsages(
        startDate,
        endDate,
        configId,
        page,
        perPage,
      );

      dispatch(mainActions.setLLMUsages(data));
    },
    [getLLMUsages, dispatch],
  );

  const updateCheckResult = useCallback(
    async (
      id: string,
      status: LLMUsageCheckResultStatus,
      correctedAnswer: string,
      correctedURL: string,
      reportIssue: boolean,
      reportIssueParams: TReportIssueParams | undefined,
      useForAIKnowledgeData: boolean,
      isReoptimization = false,
      requestedLLMBlockId?: string,
      requestedHotTopicId?: string,
      isPriorityKnowledge = false,
    ): Promise<TCheckResult | undefined> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));
        const body = {
          id,
          status,
          correctedAnswer,
          correctedURL,
          reportIssue,
          reportIssueParams,
          useForAIKnowledgeData,
          // Priority Knowledge routing applies to any dataset-feeding answer (Good or
          // corrected), so it is NOT zeroed below alongside the correction-only fields.
          isPriorityKnowledge,
          isReoptimization,
          requestedLLMBlockId,
          requestedHotTopicId,
        };

        // * If the status is not needs improvement, zero fields only used by needs improvement
        if (status !== LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
          Object.assign(body, {
            correctedAnswer: '',
            correctedURL: '',
            reportIssue: false,
            reportIssueParams: undefined,
          });
        }

        const { data } = await client.llmUsages.updateCheckResult({
          body,
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const bulkUpdateCheckResult = useCallback(
    async (
      updates: {
        id: string;
        status: LLMUsageCheckResultStatus;
        correctedAnswer?: string;
        correctedURL?: string;
        reportIssue?: boolean;
        reportIssueParams?: TReportIssueParams;
        useForAIKnowledgeData: boolean;
        isPriorityKnowledge?: boolean;
        isReoptimization?: boolean;
        requestedLLMBlockId?: string;
        requestedHotTopicId?: string;
      }[],
    ): Promise<TCheckResult[]> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const transformedUpdates = updates.map((update) => ({
          id: update.id,
          status: update.status,
          correctedAnswer: update.correctedAnswer || '',
          correctedURL: update.correctedURL || '',
          reportIssue: update.reportIssue || false,
          reportIssueParams: update.reportIssueParams,
          useForAIKnowledgeData: update.useForAIKnowledgeData,
          isPriorityKnowledge: update.isPriorityKnowledge || false,
          isReoptimization: update.isReoptimization || false,
          requestedLLMBlockId: update.requestedLLMBlockId,
          requestedHotTopicId: update.requestedHotTopicId,
        }));

        const { data } = await client.llmUsages.bulkUpdateCheckResult({
          body: transformedUpdates,
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        return [];
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getWeeklyOverview = useCallback(
    async (
      configId: string,
      year: number,
      page: number,
      perPage: number,
      llmBlockId?: string,
      shouldReturnLLMBlocks?: boolean,
      hotTopicId?: string,
      shouldReturnHotTopics?: boolean,
    ): Promise<TWeeklyOverviewResponse> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.llmUsages.getWeeklyOverview({
          query: {
            configId,
            llmBlockId,
            year: year.toString(),
            page: page.toString(),
            perPage: perPage.toString(),
            shouldReturnLLMBlocks: shouldReturnLLMBlocks?.toString(),
            ...(hotTopicId && hotTopicId !== 'all' ? { hotTopicId } : {}),
            shouldReturnHotTopics: shouldReturnHotTopics?.toString(),
          },
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return {
          results: [],
          totalRecords: 0,
        };
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getWeeklyStatistics = useCallback(
    async (
      configId: string,
      year: number,
      week: number,
      llmBlockId?: string,
    ): Promise<TWeeklyStatisticsResponse> => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } = await client.llmUsages.getWeeklyStatistics({
          query: {
            configId,
            year: year.toString(),
            week: week.toString(),
            llmBlockId,
          },
        });

        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return {
          weekNumber: 0,
          goodAnswers: 0,
          needsImprovementAnswers: 0,
          irrelevantAnswers: 0,
          notEvaluatedAnswers: 0,
          alreadyEvaluatedAnswers: {
            good: 0,
            needsImprovement: 0,
            irrelevant: 0,
          },
          totalAnswers: 0,
        };
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const translateConversation = useCallback(
    async (
      id: string,
      question: string,
      answer: string,
    ): Promise<TTranslateConversationResponse | undefined> => {
      try {
        const { data } = await client.llmUsages.translateConversation({
          params: { id },
          body: { question, answer },
        });
        return data;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [],
  );

  return {
    fetchLLMConversations,
    getLLMUsages,
    downloadLLMUsagesHistory,
    updateCheckResult,
    getWeeklyOverview,
    getConversationsByWeeks,
    getDataForDataSourceGeneration,
    getWeeklyStatistics,
    getConversationsByIds,
    bulkUpdateCheckResult,
    translateConversation,
  };
};
